import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Activity, Plus, Trash2, RefreshCw, Loader2, Send, CheckCircle2, XCircle, AlertTriangle, Gauge } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

function Toggle({ value, onChange, testId }) {
  return (
    <button onClick={() => onChange(!value)} className={`w-11 h-6 rounded-full transition-colors ${value ? "bg-[#10B981]" : "bg-[#CBD5E1]"}`} data-testid={testId}>
      <div className={`w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${value ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

export default function OPCUAGatewayPage({ onLogout, userRole, isFeatureEnabled }) {
  const [config, setConfig] = useState(null);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState(null);
  const [addTagOpen, setAddTagOpen] = useState(false);
  const [tagForm, setTagForm] = useState({ node_id: "", name: "", unit: "", machine_number: "", alarm_low: "", alarm_high: "" });

  const fetchAll = useCallback(async () => {
    try {
      const [cfgResp, statusResp] = await Promise.all([
        axios.get(`${API}/admin/opcua-config`, { headers: getAuthHeaders() }),
        axios.get(`${API}/opcua/status`, { headers: getAuthHeaders() }),
      ]);
      setConfig(cfgResp.data);
      setTags(statusResp.data.tags || []);
    } catch { toast.error("Failed to load OPC UA config"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/opcua-config`, config, { headers: getAuthHeaders() });
      toast.success("OPC UA configuration saved");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const resp = await axios.post(`${API}/admin/opcua-test`, {}, { headers: getAuthHeaders() });
      setTestResult(resp.data);
      if (resp.data.status === "connected") toast.success(resp.data.message);
      else toast.warning(resp.data.message);
    } catch (err) { setTestResult({ status: "error", message: err.response?.data?.detail || "Test failed" }); toast.error("Connection test failed"); }
    finally { setTesting(false); }
  };

  const handlePollNow = async () => {
    setPolling(true); setPollResult(null);
    try {
      const resp = await axios.post(`${API}/opcua/poll-now`, {}, { headers: getAuthHeaders() });
      setPollResult(resp.data);
      toast.success(`Read ${resp.data.read} tag(s)`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Poll failed"); }
    finally { setPolling(false); }
  };

  const handleAddTag = async () => {
    if (!tagForm.node_id.trim()) { toast.error("Node ID is required"); return; }
    try {
      await axios.post(`${API}/opcua/tags`, {
        ...tagForm,
        alarm_low: tagForm.alarm_low ? parseFloat(tagForm.alarm_low) : null,
        alarm_high: tagForm.alarm_high ? parseFloat(tagForm.alarm_high) : null,
      }, { headers: getAuthHeaders() });
      toast.success("Tag added");
      setAddTagOpen(false);
      setTagForm({ node_id: "", name: "", unit: "", machine_number: "", alarm_low: "", alarm_high: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to add tag"); }
  };

  const handleDeleteTag = async (tagId, name) => {
    if (!window.confirm(`Delete tag "${name}"? History will also be removed.`)) return;
    try {
      await axios.delete(`${API}/opcua/tags/${tagId}`, { headers: getAuthHeaders() });
      toast.success("Tag deleted");
      fetchAll();
    } catch { toast.error("Failed to delete tag"); }
  };

  const updateConfig = (k, v) => setConfig(p => ({ ...p, [k]: v }));

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#64748B]" size={24} /></div></Layout>;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="opcua-gateway-page">
        <PageHeader title="OPC UA Gateway" subtitle="Connect to plant PLCs and DCS systems for real-time machine data" testId="opcua-title" />

        {/* Server Config */}
        <div className="bg-white border border-[#E2E8F0] p-6" data-testid="opcua-config-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-lg text-[#0F172A] flex items-center gap-2"><Activity size={20} className="text-[#10B981]" /> Server Connection</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing || !config?.server_url} className="rounded-none" data-testid="opcua-test-btn">
                {testing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />} Test
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving} className="rounded-none bg-[#2563EB] text-white" data-testid="opcua-save-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2 flex items-center gap-3">
              <Label className="text-sm font-medium w-20">Enabled</Label>
              <Toggle value={config?.enabled} onChange={v => updateConfig("enabled", v)} testId="opcua-enabled" />
            </div>
            <div>
              <Label className="text-sm font-medium">OPC UA Server URL</Label>
              <Input value={config?.server_url || ""} onChange={e => updateConfig("server_url", e.target.value)} placeholder="opc.tcp://192.168.1.100:4840" className="mt-1 rounded-none font-mono text-xs" data-testid="opcua-server-url" />
            </div>
            <div>
              <Label className="text-sm font-medium">Poll Interval (seconds)</Label>
              <Input type="number" value={config?.poll_interval_seconds || 30} onChange={e => updateConfig("poll_interval_seconds", parseInt(e.target.value) || 30)} className="mt-1 rounded-none font-mono text-xs" data-testid="opcua-poll-interval" />
            </div>
            <div>
              <Label className="text-sm font-medium">Security Policy</Label>
              <select value={config?.security_policy || "None"} onChange={e => updateConfig("security_policy", e.target.value)} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm" data-testid="opcua-security-policy">
                <option value="None">None (No Security)</option>
                <option value="Basic128Rsa15">Basic128Rsa15</option>
                <option value="Basic256">Basic256</option>
                <option value="Basic256Sha256">Basic256Sha256</option>
              </select>
            </div>
            <div>
              <Label className="text-sm font-medium">Security Mode</Label>
              <select value={config?.security_mode || "None"} onChange={e => updateConfig("security_mode", e.target.value)} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm" data-testid="opcua-security-mode">
                <option value="None">None</option>
                <option value="Sign">Sign</option>
                <option value="SignAndEncrypt">Sign and Encrypt</option>
              </select>
            </div>
            <div>
              <Label className="text-sm font-medium">Username (optional)</Label>
              <Input value={config?.username || ""} onChange={e => updateConfig("username", e.target.value)} className="mt-1 rounded-none text-xs" data-testid="opcua-username" />
            </div>
            <div>
              <Label className="text-sm font-medium">Password (optional)</Label>
              <Input type="password" value={config?.password || ""} onChange={e => updateConfig("password", e.target.value)} className="mt-1 rounded-none text-xs" data-testid="opcua-password" />
            </div>
          </div>
          {testResult && (
            <div className={`mt-4 p-3 border ${testResult.status === "connected" ? "border-[#10B981] bg-[#F0FDF4]" : "border-[#DC2626] bg-[#FEF2F2]"}`} data-testid="opcua-test-result">
              <div className="flex items-center gap-2">
                {testResult.status === "connected" ? <CheckCircle2 size={16} className="text-[#10B981]" /> : <XCircle size={16} className="text-[#DC2626]" />}
                <span className="text-sm font-medium">{testResult.message}</span>
              </div>
              {testResult.endpoints && <p className="text-xs text-[#64748B] mt-1">Endpoints: {testResult.endpoints} | Server State: {testResult.server_state}</p>}
            </div>
          )}
        </div>

        {/* Tags */}
        <div className="bg-white border border-[#E2E8F0]" data-testid="opcua-tags-panel">
          <div className="p-6 border-b border-[#E2E8F0] flex items-center justify-between">
            <h2 className="font-bold text-lg text-[#0F172A] flex items-center gap-2"><Gauge size={20} className="text-[#D97706]" /> Monitored Tags ({tags.length})</h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handlePollNow} disabled={polling || tags.length === 0} className="rounded-none" data-testid="opcua-poll-btn">
                {polling ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCw size={14} className="mr-1" />} Poll Now
              </Button>
              <Button size="sm" onClick={() => setAddTagOpen(true)} className="rounded-none bg-[#D97706] text-white" data-testid="opcua-add-tag-btn">
                <Plus size={14} className="mr-1" /> Add Tag
              </Button>
            </div>
          </div>
          {tags.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#94A3B8]">No tags configured. Add OPC UA node IDs to start monitoring.</div>
          ) : (
            <div className="divide-y divide-[#E2E8F0]">
              {tags.map(tag => (
                <div key={tag.id} className="p-4 flex items-center justify-between hover:bg-[#FAFAFA]" data-testid={`opcua-tag-${tag.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${tag.status === "ok" ? "bg-[#10B981]" : tag.status === "error" ? "bg-[#DC2626]" : "bg-[#CBD5E1]"}`} />
                      <span className="font-bold text-sm text-[#0F172A]">{tag.name}</span>
                      <span className="text-[10px] font-mono text-[#94A3B8] bg-[#F8FAFC] px-1.5 py-0.5">{tag.node_id}</span>
                    </div>
                    <div className="flex gap-4 mt-1 text-xs text-[#64748B]">
                      {tag.last_value != null && (
                        <span className="font-mono font-bold text-[#0F172A] text-sm">
                          {typeof tag.last_value === "number" ? tag.last_value.toFixed(2) : tag.last_value}
                          {tag.unit && <span className="text-[#64748B] font-normal ml-0.5">{tag.unit}</span>}
                        </span>
                      )}
                      {tag.machine_number && <span>Machine: {tag.machine_number}</span>}
                      {tag.alarm_low != null && <span>Low: {tag.alarm_low}</span>}
                      {tag.alarm_high != null && <span>High: {tag.alarm_high}</span>}
                      {tag.last_read_at && <span>Last: {new Date(tag.last_read_at).toLocaleTimeString()}</span>}
                    </div>
                    {tag.status === "error" && tag.last_error && (
                      <p className="text-xs text-[#DC2626] mt-1 flex items-center gap-1"><AlertTriangle size={12} /> {tag.last_error}</p>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleDeleteTag(tag.id, tag.name)} className="rounded-none border-[#DC2626] text-[#DC2626]" data-testid={`delete-tag-${tag.id}`}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {pollResult && (
          <div className="bg-[#F0FDF4] border border-[#10B981] p-4" data-testid="poll-result">
            <p className="text-sm font-bold text-[#10B981]">Poll Complete: {pollResult.read} read, {pollResult.errors} errors</p>
            {pollResult.values?.map((v, i) => (
              <span key={i} className="inline-block text-xs bg-white border border-[#E2E8F0] px-2 py-1 mr-2 mt-1 font-mono">{v.name}: {v.value} {v.unit}</span>
            ))}
          </div>
        )}

        {/* Info */}
        <div className="bg-[#F0F9FF] border border-[#BAE6FD] p-4">
          <h3 className="font-bold text-sm text-[#0369A1] mb-2">About OPC UA</h3>
          <p className="text-xs text-[#0C4A6E]">OPC Unified Architecture is the industry standard for secure, reliable machine-to-machine communication in industrial automation. Connect to Siemens, Allen-Bradley, Honeywell, ABB, and other PLCs/DCS systems for real-time process data.</p>
        </div>
      </div>

      {/* Add Tag Dialog */}
      <Dialog open={addTagOpen} onOpenChange={setAddTagOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-md" data-testid="add-tag-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0F172A]">Add OPC UA Tag</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Configure a node to monitor from the OPC UA server.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Node ID *</Label>
              <Input value={tagForm.node_id} onChange={e => setTagForm(f => ({ ...f, node_id: e.target.value }))} placeholder="ns=2;s=Temperature" className="mt-1 rounded-none font-mono text-xs" data-testid="tag-node-id" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider">Display Name</Label>
              <Input value={tagForm.name} onChange={e => setTagForm(f => ({ ...f, name: e.target.value }))} placeholder="Reactor Temperature" className="mt-1 rounded-none text-xs" data-testid="tag-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Unit</Label>
                <Input value={tagForm.unit} onChange={e => setTagForm(f => ({ ...f, unit: e.target.value }))} placeholder="°F" className="mt-1 rounded-none text-xs" data-testid="tag-unit" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Machine #</Label>
                <Input value={tagForm.machine_number} onChange={e => setTagForm(f => ({ ...f, machine_number: e.target.value }))} className="mt-1 rounded-none font-mono text-xs" data-testid="tag-machine" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Alarm Low</Label>
                <Input type="number" value={tagForm.alarm_low} onChange={e => setTagForm(f => ({ ...f, alarm_low: e.target.value }))} className="mt-1 rounded-none text-xs" data-testid="tag-alarm-low" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Alarm High</Label>
                <Input type="number" value={tagForm.alarm_high} onChange={e => setTagForm(f => ({ ...f, alarm_high: e.target.value }))} className="mt-1 rounded-none text-xs" data-testid="tag-alarm-high" />
              </div>
            </div>
            <Button onClick={handleAddTag} disabled={!tagForm.node_id.trim()} className="w-full bg-[#10B981] hover:bg-[#059669] text-white rounded-none" data-testid="tag-submit-btn">Add Tag</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
